import { sendGTMEvent } from '@next/third-parties/google';

/**
 * Custom conversion names from the marketing agency's GTM container.
 * Meta blocks standard events such as Lead on this healthcare site, so GTM
 * forwards these dataLayer events to the pixel as custom conversions.
 */
export const GTM_EVENTS = {
  getStartedClick: 'GetStartedClick',
  browsePlanClick: 'BrowsePlanClick',
  reachedEpo: 'ReachedEPO',
} as const;

export type GtmEventName = (typeof GTM_EVENTS)[keyof typeof GTM_EVENTS];

const CLICK_EVENTS = new Set<string>([
  GTM_EVENTS.getStartedClick,
  GTM_EVENTS.browsePlanClick,
]);

export function pushGtmEvent(event: GtmEventName) {
  if (typeof window === 'undefined') return;
  sendGTMEvent({ event });
}

function clickableText(element: Element): string {
  return (element.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Map a click to a GTM conversion.
 * "browse plans in your area" only opens a confirm modal, so that click waits
 * for "Browse plans in my area".
 */
export function gtmEventForClickTarget(
  target: EventTarget | null
): GtmEventName | null {
  if (!(target instanceof Element)) return null;
  const clickable = target.closest('a, button');
  if (!clickable) return null;

  const explicit = clickable.getAttribute('data-gtm-event');
  if (explicit && CLICK_EVENTS.has(explicit)) {
    return explicit as GtmEventName;
  }

  const text = clickableText(clickable);
  if (/^get started$/i.test(text)) return GTM_EVENTS.getStartedClick;
  if (/browse plans/i.test(text) && !/in your area/i.test(text)) {
    return GTM_EVENTS.browsePlanClick;
  }
  return null;
}

let trackingEpoArrival = false;

export function resetGtmPageTrackingForTests() {
  trackingEpoArrival = false;
}

/** Fire once each time the shopper arrives on /epo, including after they leave. */
export function consumeReachedEpo(pathname: string): boolean {
  if (pathname !== '/epo') {
    trackingEpoArrival = false;
    return false;
  }
  if (trackingEpoArrival) return false;
  trackingEpoArrival = true;
  return true;
}
